"use client";

import { useRef } from "react";
import { PINK_BAND, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "./kit";

/** Workspace takeout shipped (lib/roadmap.ts `takeout`, backend#479): the
 *  dashboard's Settings → Export and GET /v1/export/* stream every link as
 *  JSON, CSV, nginx or Caddy, and the click log as CSV or JSON. The sheet
 *  names exactly those files — nginx/Caddy drop the rules they can't express. */
const TODAY: { label: string; body: string }[] = [
  {
    label: "Your domain",
    body: "It stays registered to you. Re-point its DNS record any time — nothing about the name is locked to rii.",
  },
  {
    label: "Every link",
    body: "Settings → Export downloads the whole workspace as JSON (lossless, rules included) or CSV. Workspace owners and admins can run it.",
  },
  {
    label: "Re-hosting rules",
    body: "The same links as nginx or Caddy rule files. Rules those servers can't express, like geo or device splits, are left out with a comment, and the link serves its fallback.",
  },
  {
    label: "Click history",
    body: "The per-click request log as CSV or JSON, for any window inside your plan's analytics retention.",
  },
];

/**
 * Ownership statement — the page's pink beat. One bold claim over a white
 * sheet card: what you can take with you, today.
 */
export default function OwnershipBandSection() {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Whole-row sizing (head gets a centered breathing row; the sheet is
  // `data-snug` — its own card chrome insets the content, it only needs the
  // whole-row snap), then row alignment on the settled heights.
  useWholeRowBlocks(sheetRef);
  useBandRowAlign(sheetRef);

  // md:pt-px — with no real top padding the aligner alone spaces the head:
  // exactly one free pink row above it, matching the dark band's rhythm.
  // md:pb-px — the aligner's one-cell margin after the sheet IS the band's
  // single free bottom row, and the section then ENDS on a row line (+1px)
  // so the blue roadmap wash butts against it seamlessly. pb-0 would let the
  // sheet's bottom margin collapse OUT of the section (block flow) and the
  // band would end flush at the sheet's clearing edge.
  return (
    <SectionShell
      id="ref-ownership"
      band="pink"
      decoAvoid
      className="max-md:bg-[#d70e62] md:pt-px md:pb-px"
    >
      <SectionHead
        center
        tone="pink"
        gridFill={PINK_BAND}
        eyebrow="You own it"
        title={<>Your links. Your data. Your exit.</>}
        lead="Your domain is always yours — point it anywhere, any time. Export your links and your click history from the dashboard or the API, whenever you want."
      />

      <div
        ref={sheetRef}
        data-grid-block
        data-grid-fill={PINK_BAND}
        data-snug
        className="relative mx-auto w-full max-w-[760px] overflow-hidden rounded-ref-lg bg-surface p-5 shadow-ref-lg"
      >
        <ul className="flex flex-col divide-y divide-line">
          {TODAY.map((t) => (
            <li key={t.label} className="flex flex-col gap-1 py-3.5 first:pt-1 last:pb-1 sm:flex-row sm:gap-4">
              <span className="w-[150px] shrink-0 font-jetbrains text-[12.5px] font-semibold uppercase tracking-[0.06em] text-brand-600">
                {t.label}
              </span>
              <span className="text-[14.5px] leading-[1.55] text-muted">{t.body}</span>
            </li>
          ))}
        </ul>
      </div>
    </SectionShell>
  );
}
